import React, { useState } from 'react';
import { ScriptResponse, recordScriptInteraction } from '@/api';
import ScriptRegenerationModal from './ScriptRegenerationModal';

interface Props {
  script: ScriptResponse;
  projectId: string;
  onScriptUpdated: (script: ScriptResponse, improvement?: any) => void;
  onAccept?: () => void;
}

export default function ScriptDisplay({ script, projectId, onScriptUpdated, onAccept }: Props) {
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [isLoved, setIsLoved] = useState(false);

  const handleLoveScript = async () => {
    try {
      await recordScriptInteraction({
        projectId,
        interactionType: 'loved'
      });
      setIsLoved(true);
    } catch (error) {
      console.error('Failed to record love interaction:', error);
    }
  };

  const handleScriptRegenerated = (newScript: ScriptResponse, improvement: any) => {
    onScriptUpdated(newScript, improvement);
    setShowRegenerateModal(false);
  };

  const formatTimestamp = (timestamp: string) => {
    return timestamp.replace(/[\[\]]/g, '');
  };

  const getRetentionScoreColor = (score: number) => {
    if (score >= 95) return 'text-emerald-600 bg-emerald-50 border-emerald-200';
    if (score >= 85) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 75) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  return (
    <div className="space-y-6">
      {/* Header with Metadata */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-4 gap-4">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{script.metadata.title}</h2>
            <p className="text-gray-600">{script.metadata.description}</p>
          </div>
          
          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Love Button */}
            <button
              onClick={handleLoveScript}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                isLoved 
                  ? 'bg-pink-50 border-pink-200 text-pink-700' 
                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-pink-50 hover:border-pink-200 hover:text-pink-700'
              }`}
            >
              <svg className="w-4 h-4" fill={isLoved ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              {isLoved ? 'Loved!' : 'Love'}
            </button>

            {/* Regenerate Button */}
            <button
              onClick={() => setShowRegenerateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Regenerate
            </button>

            {/* Accept & Continue Button */}
            {onAccept && (
              <button
                onClick={onAccept}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Accept & Continue
              </button>
            )}
          </div>
        </div>

        {/* Metadata Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-lg font-semibold text-gray-900">{script.metadata.estimatedDuration}</div>
            <div className="text-sm text-gray-600">Duration</div>
          </div>
          
          <div className={`text-center p-3 rounded-lg border ${getRetentionScoreColor(script.metadata.retentionScore)}`}>
            <div className="text-lg font-semibold">{script.metadata.retentionScore}/100</div>
            <div className="text-sm">Retention Score</div>
          </div>
          
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-lg font-semibold text-gray-900">{script.metadata.complexity}</div>
            <div className="text-sm text-gray-600">Complexity</div>
          </div>
          
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-lg font-semibold text-gray-900">{script.script.sections.length}</div>
            <div className="text-sm text-gray-600">Sections</div>
          </div>
        </div>

        {/* Retention Reasoning */}
        {script.metadata.retentionReasoning && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-medium text-blue-900 mb-1">Retention Analysis</h4>
            <p className="text-sm text-blue-800">{script.metadata.retentionReasoning}</p>
          </div>
        )}
      </div>

      {/* Script Content */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Script Content</h3>
        
        <div className="space-y-4">
          {script.script.sections.map((section, index) => (
            <div key={index} className="border-l-4 border-blue-500 pl-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <h4 className="font-medium text-gray-900">{section.title}</h4>
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                    {formatTimestamp(section.timestamp)}
                  </span>
                </div>
              </div>
              
              <div className="prose prose-sm max-w-none mb-3">
                <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">{section.content}</p>
              </div>
              
              {section.visualCues && section.visualCues.length > 0 && (
                <div className="mb-2">
                  <h5 className="text-sm font-medium text-gray-700 mb-1">Visual Cues:</h5>
                  <div className="flex flex-wrap gap-2">
                    {section.visualCues.map((cue, cueIndex) => (
                      <span key={cueIndex} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                        {cue}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {section.keyTakeaway && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
                  <h5 className="text-sm font-medium text-yellow-800 mb-1">Key Takeaway:</h5>
                  <p className="text-sm text-yellow-700">{section.keyTakeaway}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Production Notes - Only show if available */}
      {script.productionNotes && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Production Notes</h3>
          
          {script.productionNotes.requiredAssets && script.productionNotes.brollSuggestions && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Required Assets</h4>
                <ul className="space-y-1">
                  {script.productionNotes.requiredAssets.map((asset, index) => (
                    <li key={index} className="text-sm text-gray-600 flex items-start gap-2">
                      <span className="text-blue-500">•</span>
                      {asset}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-2">B-roll Suggestions</h4>
                <ul className="space-y-1">
                  {script.productionNotes.brollSuggestions.map((suggestion, index) => (
                    <li key={index} className="text-sm text-gray-600 flex items-start gap-2">
                      <span className="text-green-500">•</span>
                      {suggestion}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          
          {(script.productionNotes.estimatedProductionTime || script.productionNotes.difficulty || script.productionNotes.specialRequirements) && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              {script.productionNotes.estimatedProductionTime && (
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-900">{script.productionNotes.estimatedProductionTime}</div>
                  <div className="text-xs text-gray-600">Production Time</div>
                </div>
              )}
              
              {script.productionNotes.difficulty && (
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-900">{script.productionNotes.difficulty}</div>
                  <div className="text-xs text-gray-600">Difficulty</div>
                </div>
              )}
              
              {script.productionNotes.specialRequirements && (
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-900">{script.productionNotes.specialRequirements.length || 0}</div>
                  <div className="text-xs text-gray-600">Special Requirements</div>
                </div>
              )}
            </div>
          )}

          {script.productionNotes.specialRequirements && script.productionNotes.specialRequirements.length > 0 && (
            <div className="mt-4">
              <h4 className="font-medium text-gray-900 mb-2">Special Requirements</h4>
              <div className="flex flex-wrap gap-2">
                {script.productionNotes.specialRequirements.map((req, index) => (
                  <span key={index} className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
                    {req}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tags */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">SEO Tags</h3>
        <div className="flex flex-wrap gap-2">
          {script.metadata.tags.map((tag, index) => (
            <span key={index} className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">
              #{tag}
            </span>
          ))}
        </div>
      </div>

      {/* Regeneration Modal */}
      <ScriptRegenerationModal
        open={showRegenerateModal}
        onClose={() => setShowRegenerateModal(false)}
        onScriptRegenerated={handleScriptRegenerated}
        originalScript={script}
        projectId={projectId}
        currentRetentionScore={script.metadata.retentionScore}
      />
    </div>
  );
}